import { format } from 'date-fns';
// @ts-ignore - date-fns-tz doesn't have proper TypeScript types
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Convert a UTC date string to a formatted string in the user's timezone
 * @param utcDateString - The UTC date string from the database
 * @param userTimezone - The user's timezone (e.g., 'America/Toronto')
 * @param formatString - The format string (defaults to 'MMM dd, HH:mm:ss')
 * @returns Formatted date string in the user's timezone
 */
export function formatInUserTimezone(
  utcDateString: string,
  userTimezone: string = 'America/Toronto',
  formatString: string = 'MMM dd, HH:mm:ss'
): string {
  try {
    // Parse the UTC date string
    const utcDate = new Date(utcDateString);
    
    // Format directly in the target timezone
    return formatInTimeZone(utcDate, userTimezone, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fallback to basic formatting if there's an error
    return format(new Date(utcDateString), formatString);
  }
}

/**
 * Get timezone display name
 * @param timezone - IANA timezone identifier (e.g., 'America/Toronto')
 * @returns Human-readable timezone name
 */
export function getTimezoneDisplayName(timezone: string): string {
  const timezoneMap: Record<string, string> = {
    'America/Vancouver': 'Pacific Time (BC)',
    'America/Edmonton': 'Mountain Time (AB)',
    'America/Regina': 'Central Time (SK - no DST)',
    'America/Winnipeg': 'Central Time (MB)',
    'America/Toronto': 'Eastern Time (ON/QC)',
    'America/Halifax': 'Atlantic Time (NS/NB/PE)',
    'America/St_Johns': 'Newfoundland Time (NL)',
    'America/New_York': 'Eastern Time (US)',
    'America/Chicago': 'Central Time (US)',
    'America/Denver': 'Mountain Time (US)',
    'America/Los_Angeles': 'Pacific Time (US)',
    'America/Phoenix': 'Mountain Time (AZ - no DST)',
  };
  
  return timezoneMap[timezone] || timezone;
}

/**
 * Get current time in user's timezone with timezone abbreviation
 * @param timezone - IANA timezone identifier
 * @returns Current time string with timezone abbreviation (e.g., "2:34 PM EDT")
 */
export function getCurrentTimeInTimezone(timezone: string): string {
  try {
    return new Date().toLocaleString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  } catch (error) {
    console.error('Error getting current time:', error);
    return new Date().toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}